import { useEffect, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { useParams, Link as RouterLink } from "react-router-dom";
import { usePanelTokens } from "../../../utils/panelTokens";
import { useAuthGate } from "../../../hooks/useAuthGate";
import { listMotors } from "./api";
import { basePath, comboPath, motorSlug } from "./catalog";
import SubmitPartDialog from "../propulsionCalc/SubmitPartDialog";

const SPECS = [
  { key: "kv", label: "KV", unit: " rpm/V" },
  { key: "weightG", label: "Weight", unit: " g" },
  { key: "maxCurrentA", label: "Max current", unit: " A" },
  { key: "poles", label: "Magnetic poles", unit: "" },
  { key: "statorSize", label: "Stator size", unit: "" },
  { key: "resistanceMohm", label: "Internal resistance", unit: " mΩ" },
];

/**
 * A single motor.
 *
 * Exists because most of the catalog has no bench run yet, and those cards
 * still need somewhere to go — a page that says plainly "these are the
 * manufacturer's figures, we have not measured this one" is honest and useful,
 * where an unclickable card is just a dead end. When a published comparison
 * does exist, this page hands off to it.
 */
const MotorDetail = () => {
  const { slug } = useParams();
  const t = usePanelTokens();
  const { requireSignin } = useAuthGate();
  const [motor, setMotor] = useState(undefined);
  const [submitOpen, setSubmitOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    listMotors().then(({ motors }) => {
      if (!alive) return;
      setMotor(motors.find((m) => motorSlug(m.brand, m.name) === slug) || null);
    });
    return () => {
      alive = false;
    };
  }, [slug]);

  if (motor === undefined) {
    return (
      <Box sx={{ background: t.ground, minHeight: "100%", display: "flex", justifyContent: "center", pt: 10 }}>
        <CircularProgress size={26} sx={{ color: t.accent }} />
      </Box>
    );
  }

  if (!motor) {
    return (
      <Box sx={{ background: t.ground, color: t.text, minHeight: "100%", p: 7, textAlign: "center" }}>
        <Box sx={{ fontSize: 20, fontWeight: 700, color: t.textStrong, mb: 2 }}>Motor not found</Box>
        <Button component={RouterLink} to={basePath()} sx={{ color: t.accent, textTransform: "none" }}>
          Back to all motors
        </Button>
      </Box>
    );
  }

  const values = {
    ...motor,
    resistanceMohm: motor.resistanceOhm != null ? (motor.resistanceOhm * 1000).toFixed(1) : null,
  };
  const known = SPECS.filter((s) => values[s.key] !== null && values[s.key] !== undefined);

  return (
    <Box sx={{ background: t.ground, color: t.text, minHeight: "100%" }}>
      <Box sx={{ px: { xs: 3, md: 7 }, pt: { xs: 4, md: 6 }, pb: { xs: 4, md: 5 }, borderBottom: `1px solid ${t.border}` }}>
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          <Button
            component={RouterLink}
            to={basePath()}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            sx={{ color: t.textMuted, textTransform: "none", mb: 2, ml: -1, "&:hover": { color: t.accent, background: "transparent" } }}
          >
            All motors
          </Button>

          <Box sx={{ fontSize: 11, letterSpacing: ".12em", color: t.accent, fontWeight: 700, mb: 1 }}>
            {String(motor.brand).toUpperCase()}
          </Box>
          <Box component="h1" sx={{ m: 0, fontSize: { xs: 24, md: 34 }, fontWeight: 700, color: t.textStrong, letterSpacing: "-0.01em" }}>
            {motor.name}
          </Box>
          <Box sx={{ mt: 1, fontSize: 14, color: t.textMuted }}>
            Manufacturer specifications — published by {motor.brand}, not measured by us.
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 3, md: 7 }, py: { xs: 4, md: 5 }, maxWidth: 900, mx: "auto" }}>
        {known.length > 0 ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 2, mb: 4 }}>
            {known.map((s) => (
              <Box key={s.key} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${t.border}`, background: t.surface }}>
                <Box sx={{ fontSize: 11, letterSpacing: ".08em", color: t.textFaint, mb: 0.8 }}>
                  {s.label.toUpperCase()}
                </Box>
                <Box sx={{ fontSize: 22, fontWeight: 700, color: t.textStrong }}>
                  {values[s.key]}
                  <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: t.textMuted }}>{s.unit}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ mb: 4, fontSize: 14, color: t.textMuted }}>
            No detailed specifications published for this motor.
          </Box>
        )}

        {motor.comboSlug ? (
          <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, border: `1px solid ${t.accent}`, background: `${t.accent}0F`, display: "flex", gap: 2.5, alignItems: "center", flexWrap: "wrap" }}>
            <CompareArrowsIcon sx={{ color: t.accent, fontSize: 28 }} />
            <Box sx={{ flex: "1 1 320px" }}>
              <Box sx={{ fontSize: 17, fontWeight: 700, color: t.textStrong, mb: 0.5 }}>
                We put this one on the stand
              </Box>
              <Box sx={{ fontSize: 14, color: t.textMuted, lineHeight: 1.6 }}>
                See the manufacturer's published table beside our measured run,
                with the delta at every throttle point.
              </Box>
            </Box>
            <Button
              component={RouterLink}
              to={comboPath(motor.comboSlug)}
              variant="contained"
              sx={{ background: t.accent, color: t.onAccent, textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3, "&:hover": { background: t.accentHover } }}
            >
              Claimed vs measured
            </Button>
          </Box>
        ) : (
          <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, border: `1px solid ${t.border}`, background: t.surfaceAlt, display: "flex", gap: 2.5, alignItems: "flex-start", flexWrap: "wrap" }}>
            <ScienceOutlinedIcon sx={{ color: t.accent, fontSize: 26, mt: 0.3 }} />
            <Box sx={{ flex: "1 1 320px" }}>
              <Box sx={{ fontSize: 17, fontWeight: 700, color: t.textStrong, mb: 0.8 }}>
                Not on our thrust stand yet
              </Box>
              <Box sx={{ fontSize: 14, color: t.textMuted, lineHeight: 1.6 }}>
                Everything above is what {motor.brand} publishes. We have not
                measured this motor, so there is nothing to compare it against —
                and we would rather say so than show you a number we did not take.
              </Box>
            </Box>
            <Button
              onClick={() => requireSignin(() => {})}
              variant="contained"
              sx={{ background: t.accent, color: t.onAccent, textTransform: "none", fontWeight: 600, borderRadius: 2, "&:hover": { background: t.accentHover } }}
            >
              Request this test
            </Button>
          </Box>
        )}

        {motor.datasheetUrl && (
          <Box sx={{ mt: 2.5, fontSize: 13, color: t.textFaint, display: "flex", alignItems: "center", gap: 0.6 }}>
            Source:
            <Box
              component="a"
              href={motor.datasheetUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: t.accent, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 0.4, "&:hover": { textDecoration: "underline" } }}
            >
              {motor.brand} product page
              <OpenInNewIcon sx={{ fontSize: 13 }} />
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 4, p: 3, borderRadius: 2, border: `1px solid ${t.border}`, background: t.surface, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Box sx={{ fontSize: 15, fontWeight: 700, color: t.textStrong, mb: 0.5 }}>
              Is this your motor?
            </Box>
            <Box sx={{ fontSize: 13.5, color: t.textMuted }}>
              Send your published thrust table and we'll show it here, with attribution.
            </Box>
          </Box>
          <Button
            onClick={() => setSubmitOpen(true)}
            variant="outlined"
            sx={{ color: t.accent, borderColor: t.accent, textTransform: "none", fontWeight: 600, borderRadius: 2, "&:hover": { borderColor: t.accent, background: `${t.accent}14` } }}
          >
            Submit data
          </Button>
        </Box>
      </Box>

      <SubmitPartDialog open={submitOpen} onClose={() => setSubmitOpen(false)} />
    </Box>
  );
};

export default MotorDetail;
